/**
 * LabelConfigDrawer
 *
 * Drawer "Automacoes da etiqueta" — gerencia auto-disparo (quando contato cai
 * na etiqueta) + regras de aproximacao (reagir a mensagem recebida).
 *
 * Estilo passo-a-passo alinhado com ScheduleMessageModal (single mode no
 * drawer). 600px de largura. Detecta canal ativo (WA Business vs QR) via
 * useActiveConnections para esconder anti-bloqueio/digitacao quando nao
 * aplicavel (so WA QR), e mostrar template fallback Meta quando WA Business.
 *
 * Backend contract:
 *   PUT /api/users/me/labels/{label_id} aceita auto_dispatch + rules.
 *   auto_dispatch suporta:
 *     - inline custom: text + files
 *     - inline AI: message_type=ai_generated + instruction + ai opts
 *     - legacy: followup_id (atrelar a um followup existente)
 *     - business_config implicito via fallback_template_name (WA Business)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Settings, Zap, Filter, Plus, Trash2, Loader2, AlertCircle, Check,
  Paperclip, Image, Film, Music, FileText, ChevronDown, ChevronUp,
  ShieldCheck, Send, Sparkles, Lock, Link2, AlertTriangle,
  Shuffle, Target, Bot, User, Inbox, Tag, Trophy, XCircle, CircleDashed,
  ArrowLeftRight, Flag,
} from 'lucide-react';
import authService from '../../services/global/authService';
import followupService from '../../services/global/followupService';
import fileUploadService from '../../services/global/fileUploadService';
import templateService from '../../services/global/templateService';
import { templateRequiresParams } from '../../utils/templateVariables';
import assignmentService from '../../services/global/assignmentService';
import ChipMultiSelect from '../common/ChipMultiSelect';
import contactTagsService from '../../services/global/contactTagsService';
import customFieldsService from '../../services/global/customFieldsService';
import useActiveConnections from '../../hooks/useActiveConnections';
import StageTrailSection from './StageTrailSection';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import TemplatePicker from '../messages/TemplatePicker';
import {
  analyzeTemplate, findTemplate, uniqueApproved, templateCapabilities, templateUsability,
} from '../../utils/templateParams';
import { contarRegrasSalvas, mostrarRegrasAntigas } from './legacyRules';
import {
  opcoesDeEtapa, opcoesComSalva, lerMapaDeEntrada, mapaParaSalvar, linhasDeConexao,
} from './entryByConnection';
import {
  MAX_TAGS_DE_MOTIVO, atendimentoParaSalvar, etapasDoMesmoFunil, lerAtendimento, saidaBloqueada,
} from './stageAttendance';

const FILE_ICONS = {
  image: Image,
  video: Film,
  audio: Music,
  pdf: FileText,
  document: FileText,
};

const TRIGGER_OPTIONS = [
  { value: 'keyword', label: 'Palavra-chave', help: 'Casa por substring' },
  { value: 'regex', label: 'Regex', help: 'Expressão regular' },
  { value: 'any_message', label: 'Qualquer mensagem', help: 'Sempre dispara' },
];

// target: o que a ação precisa escolher — 'label' (etiqueta), 'agent' (agente,
// opcional), 'tags' (uma ou mais tags) ou null (nada).
const ACTION_OPTIONS = [
  { value: 'assign_label', label: 'Mover para outra etapa', target: 'label' },
  { value: 'remove_label', label: 'Tirar da etapa', target: 'label' },
  { value: 'leave_unlabeled', label: 'Tirar de todas as etapas', target: null },
  { value: 'activate_agent', label: 'Atrelar agente ao contato', target: 'agent' },
  { value: 'apply_tag', label: 'Aplicar tag', target: 'tags' },
  { value: 'remove_tag', label: 'Remover tag', target: 'tags' },
];

const AI_SUGGESTIONS = [
  { label: 'Apresentar produto', text: 'Apresente nossa solução de forma breve e amigável usando o primeiro nome do contato. Destaque o principal benefício e termine com uma pergunta convidando o contato a saber mais.' },
  { label: 'Boas-vindas', text: 'Dê boas-vindas ao contato usando o primeiro nome dele. Apresente brevemente nossa empresa e pergunte como podemos ajudar.' },
  { label: 'Pedir mais informações', text: 'Pergunte ao contato detalhes específicos sobre a necessidade dele para podermos atender melhor. Use o primeiro nome e seja gentil.' },
  { label: 'Reativar contato frio', text: 'Reative o contato de forma leve e sem pressão. Use o primeiro nome, mencione que faz um tempo desde a última conversa e pergunte se ele ainda tem interesse.' },
];

// Marcas de acentuacao. RegExp montado de string ASCII de proposito — o
// intervalo cru no fonte deixa o arquivo refem da codificacao do editor.
const REASON_DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

/**
 * Drawer ÚNICO de automação — usado tanto pela coluna de ENTRADA de um funil
 * (mode="entry", recebe `funnel`) quanto por uma ETIQUETA (mode="label", recebe
 * `label`). Mesmas seções nos dois: o que disparar (auto_dispatch) + distribuir
 * (responsável fixo/round-robin) + regras de aproximação. Entrada grava em
 * /api/pipelines/{id}/entry-automation; etiqueta em /api/users/me/labels/{id}.
 */
// Origem por número de entrada: a lista mistura os quatro canais, então cada
// linha diz de onde é quando não tem telefone para mostrar.
const CANAL_ROTULO = {
  wa_personal: 'WhatsApp QR Code',
  wa_business: 'WhatsApp Business',
  instagram: 'Instagram',
  messenger: 'Messenger',
  widget: 'Widget do site',
};

const LabelConfigDrawer = ({ isOpen, onClose, label, colors, onSaved, mode = 'label', funnel = null, funnelLabels = [] }) => {
  const isEntry = mode === 'entry';
  // --- auto_dispatch state ---
  const [autoDispatchEnabled, setAutoDispatchEnabled] = useState(false);
  // 'inline' = drawer escreve mensagem; 'advanced' = atrelar a followup existente
  const [autoDispatchMode, setAutoDispatchMode] = useState('inline');
  // tipo da mensagem inline
  const [messageType, setMessageType] = useState('custom'); // 'custom' | 'ai_generated'
  // custom
  const [autoDispatchText, setAutoDispatchText] = useState('');
  const [autoDispatchFiles, setAutoDispatchFiles] = useState([]);
  // ai_generated
  const [instruction, setInstruction] = useState('');
  const [useConversationContext, setUseConversationContext] = useState(true);
  const [forceResponse, setForceResponse] = useState(true);
  // template (mensagem PRINCIPAL — distinto do fallback)
  const [mainTemplateName, setMainTemplateName] = useState('');
  const [mainTemplateLanguage, setMainTemplateLanguage] = useState('pt_BR');
  // business fallback template
  const [useFallbackTemplate, setUseFallbackTemplate] = useState(false);
  const [fallbackTemplateName, setFallbackTemplateName] = useState('');
  const [fallbackTemplateLanguage, setFallbackTemplateLanguage] = useState('pt_BR');
  // shared
  const [autoDispatchAntiBlock, setAutoDispatchAntiBlock] = useState(true);
  const [autoDispatchTyping, setAutoDispatchTyping] = useState(false);
  const [autoDispatchRepeat, setAutoDispatchRepeat] = useState(false);
  const [autoDispatchFollowupId, setAutoDispatchFollowupId] = useState('');

  // --- distribution state (distribuir o contato ao entrar) ---
  const [distributionEnabled, setDistributionEnabled] = useState(false);
  const [distributionMode, setDistributionMode] = useState('round_robin'); // 'fixed' | 'round_robin'
  const [distributionFixedKey, setDistributionFixedKey] = useState(null);  // "type:id"
  const [distributionPoolKeys, setDistributionPoolKeys] = useState(new Set()); // Set("type:id")
  const [assignmentTargets, setAssignmentTargets] = useState([]);

  // --- tags (Fase B): catálogo + tags de entrada (aplicar/remover ao entrar) ---
  const [allTags, setAllTags] = useState([]);
  const [entryTagsAdd, setEntryTagsAdd] = useState([]);     // [tag_id]
  // --- Origem do lead (só entrada de funil) ---
  // field_id do campo SELECT com papel lead_source; sinais -> opção; frases ->
  // opção (1ª mensagem, antes do agente); default.
  const [leadSourceFieldId, setLeadSourceFieldId] = useState('');
  const [leadSourceBySignal, setLeadSourceBySignal] = useState({});
  const [leadSourcePhrases, setLeadSourcePhrases] = useState([]);   // [{phrase, option_id}]
  const [leadSourceDefault, setLeadSourceDefault] = useState('');
  // Origem pelo NÚMERO por onde o lead chegou (ex.: caiu no WhatsApp do
  // consultor = prospecção dele). Chaveada pela conexão; a lista vem do
  // backend para a tela não precisar saber o nome interno da instância.
  const [leadSourceByConnection, setLeadSourceByConnection] = useState({});
  const [entryConnections, setEntryConnections] = useState([]);
  const [selectFields, setSelectFields] = useState([]);              // definições tipo select
  const [entryTagsRemove, setEntryTagsRemove] = useState([]); // [tag_id]

  // --- outcome: esta etapa é o RESULTADO do funil? null | 'won' | 'lost'.
  // Independente do nome da coluna; exclusivo por funil (o backend transfere).
  const [outcome, setOutcome] = useState(null);
  // Motivos sugeridos no fechamento desta etapa. Vazio = modal só com texto
  // livre (nenhum cliente que não configurou nada muda de comportamento).
  const [reasonOptions, setReasonOptions] = useState([]);
  const [newReasonOption, setNewReasonOption] = useState('');

  // Em entry mode o config vem do backend (async); em label mode, do prop.
  const [entryLoading, setEntryLoading] = useState(false);

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAiAdvanced, setShowAiAdvanced] = useState(false);

  const [rules, setRules] = useState([]);
  // Regras de aproximação: descontinuadas na tela (item 5.7, 2026-09-13). A
  // seção só aparece se este funil/etapa JÁ tinha regra salva, e em modo
  // leitura com opção de remover. O motor no backend segue rodando.
  const [savedRulesCount, setSavedRulesCount] = useState(0);
  const [followups, setFollowups] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // Seletor único (2026-09-14): lista todos os aprovados e desabilita com o
  // motivo o que esta automação ainda não envia. Sem a flag, como antes.
  const [allBusinessTemplates, setAllBusinessTemplates] = useState([]);
  const { flags: featureFlags } = useFeatureFlags();
  const templateUi = Boolean(featureFlags.template_params_ui);
  // Multi-funil (2026-09-16): acao de entrada "copiar para outro funil"
  // (funnel_copy): ao entrar nesta etapa o contato entra tambem na etapa
  // escolhida de OUTRO funil e continua aqui. So etiqueta (nao a entrada).
  const multiFunnel = Boolean(featureFlags.multi_funnel);
  const [funnelCopyEnabled, setFunnelCopyEnabled] = useState(false);
  const [funnelCopyLabelId, setFunnelCopyLabelId] = useState('');
  // Só copia quando o contato ainda não tem etapa no funil de destino: o
  // primeiro desfecho vale e os seguintes não sobrescrevem (Relacionamento).
  const [funnelCopyOnlyIfEmpty, setFunnelCopyOnlyIfEmpty] = useState(false);
  // Atendimento que fecha sozinho (Bigens, 2026-10-22). Etapa por mensagem:
  // para onde o contato vai, a partir DESTA etapa, quando ele escreve ou quando
  // nós respondemos ('' = fica). Encerramento: ao entrar aqui, resumo da IA no
  // comentário, tag de motivo de uma lista fixa e volta ao agente.
  const messageTransitionsUi = Boolean(featureFlags.funnel_message_transitions);
  const closingUi = Boolean(featureFlags.funnel_stage_closing);
  const [transitionOnInbound, setTransitionOnInbound] = useState('');
  const [transitionOnOutbound, setTransitionOnOutbound] = useState('');
  const [closingEnabled, setClosingEnabled] = useState(false);
  const [closingSummary, setClosingSummary] = useState(true);
  const [closingReasonTagIds, setClosingReasonTagIds] = useState([]);
  const [closingReturnToAgent, setClosingReturnToAgent] = useState(true);
  const greetingCaps = templateCapabilities('greeting', featureFlags);
  const fallbackCaps = templateCapabilities('fallback', featureFlags);

  // --- Entrada por número e roleta da resposta a disparo: só na entrada do
  // funil padrão (levantamento myCar 13/09). O número DESTRAVA quer o lead novo
  // direto numa etapa; a roleta da resposta a disparo só existia pela API.
  const [connectionStageMap, setConnectionStageMap] = useState({}); // {ref: {label_id, roulette_id}}
  const [replyOwnerRouletteId, setReplyOwnerRouletteId] = useState(''); // '' = ninguém
  const [entryIsDefault, setEntryIsDefault] = useState(false);
  // As duas seções só aparecem, e só vão no payload, com a config carregada:
  // se o GET falhar, o backend mantém o que já estava salvo.
  const [entryConfigLoaded, setEntryConfigLoaded] = useState(false);
  const [funnelsList, setFunnelsList] = useState([]);
  const [roulettes, setRoulettes] = useState([]);
  const entryDefaultReady = isEntry && entryConfigLoaded && entryIsDefault;
  // Com a tela "Entrada de leads" (2026-10-14) a regra por número mora lá, num
  // lugar só para os 4 canais: aqui sobra um aviso apontando para ela.
  const entryRulesScreen = Boolean(featureFlags.entry_rules_by_connection);
  const showEntryByConnection = entryDefaultReady && Boolean(featureFlags.entry_by_connection_stage) && !entryRulesScreen;
  const showReplyOwner = entryDefaultReady && Boolean(featureFlags.entry_guard_assign_owner);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Plan limits (otimista — mesma estrategia do ScheduleMessageModal)
  const [limits, setLimits] = useState(null);
  const limitsLoaded = limits != null;
  const canCreateAiGenerated = !limitsLoaded || (limits?.can_create_ai_generated ?? false);

  // Canal ativo — detecta se WA Business e/ou QR estao conectados
  const connections = useActiveConnections({ enabled: isOpen });
  const hasBusiness = connections.business;
  // Canal escolhido para o disparo (usado no payload). Quando o usuario tem
  // os dois conectados ele optar via toggle de canal; com apenas um, e
  // determinado automaticamente.
  const [selectedChannel, setSelectedChannel] = useState('wa_personal');

  // ============================================
  // Load state on open
  // ============================================
  useEffect(() => {
    if (!isOpen) return;
    if (isEntry && !funnel) return;
    if (!isEntry && !label) return;

    // Popula todo o estado a partir de um config { auto_dispatch, distribution,
    // rules } — mesma shape para etiqueta e para entrada de funil.
    const applyConfig = (cfg) => {
      const ad = (cfg && cfg.auto_dispatch) || {};
      setAutoDispatchEnabled(!!ad.enabled);
      setAutoDispatchMode(ad.followup_id ? 'advanced' : 'inline');
      const persistedType = ad.message_type;
      setMessageType(
        persistedType === 'ai_generated' || persistedType === 'template' ? persistedType : 'custom'
      );
      setAutoDispatchText(ad.text || '');
      setAutoDispatchFiles(Array.isArray(ad.files) ? ad.files : []);
      setInstruction(ad.instruction || '');
      setUseConversationContext(ad.use_conversation_context !== false);
      setForceResponse(ad.force_response !== false);
      setMainTemplateName(ad.template_name || '');
      setMainTemplateLanguage(ad.template_language || 'pt_BR');
      setUseFallbackTemplate(!!ad.fallback_template_name);
      setFallbackTemplateName(ad.fallback_template_name || '');
      setFallbackTemplateLanguage(ad.fallback_template_language || 'pt_BR');
      setAutoDispatchAntiBlock(ad.anti_block !== undefined ? !!ad.anti_block : true);
      setAutoDispatchTyping(!!ad.typing_simulation);
      setAutoDispatchRepeat(!!ad.repeat_on_reentry);
      setAutoDispatchFollowupId(ad.followup_id || '');
      setSelectedChannel(ad.channel || 'wa_personal');

      const dist = (cfg && cfg.distribution) || {};
      setDistributionEnabled(!!dist.enabled);
      setDistributionMode(dist.mode === 'fixed' ? 'fixed' : 'round_robin');
      const ft = dist.fixed_target;
      setDistributionFixedKey(ft && ft.id ? `${ft.type}:${ft.id}` : null);
      setDistributionPoolKeys(new Set((dist.pool || []).map((p) => `${p.type}:${p.id}`)));

      const srcRules = Array.isArray(cfg && cfg.rules) ? cfg.rules : [];
      setSavedRulesCount(contarRegrasSalvas(cfg));
      setRules(srcRules.map(r => ({
        ...r,
        patterns: r.patterns?.length ? r.patterns : [''],
        action: {
          type: (r.action || {}).type || 'assign_label',
          target_label_id: (r.action || {}).target_label_id ?? null,
          target_agent_id: (r.action || {}).target_agent_id ?? null,
          target_tag_ids: (r.action || {}).target_tag_ids || [],
        },
      })));

      const et = (cfg && cfg.entry_tags) || {};
      setEntryTagsAdd(Array.isArray(et.add) ? et.add : []);
      setEntryTagsRemove(Array.isArray(et.remove) ? et.remove : []);
      const ls = (cfg && cfg.lead_source) || {};
      setLeadSourceFieldId(ls.field_id || '');
      setLeadSourceBySignal(ls.by_signal && typeof ls.by_signal === 'object' ? { ...ls.by_signal } : {});
      setLeadSourcePhrases(Array.isArray(ls.by_phrase) ? ls.by_phrase.map((p) => ({ phrase: p.phrase || '', option_id: p.option_id || '' })) : []);
      setLeadSourceDefault(ls.default || '');
      setLeadSourceByConnection(
        ls.by_connection && typeof ls.by_connection === 'object' ? { ...ls.by_connection } : {}
      );
      // Só etiqueta tem resultado — a entrada do funil não é uma coluna real.
      const oc = cfg && cfg.outcome;
      setOutcome(oc === 'won' || oc === 'lost' ? oc : null);
      const fc = (cfg && cfg.funnel_copy) || {};
      setFunnelCopyEnabled(Boolean(fc.enabled) && Boolean(fc.label_id));
      setFunnelCopyLabelId(fc.label_id || '');
      setFunnelCopyOnlyIfEmpty(Boolean(fc.only_if_empty));
      const at = lerAtendimento(cfg);
      setTransitionOnInbound(at.transitionOnInbound);
      setTransitionOnOutbound(at.transitionOnOutbound);
      setClosingEnabled(at.closingEnabled);
      setClosingSummary(at.closingSummary);
      setClosingReasonTagIds(at.closingReasonTagIds);
      setClosingReturnToAgent(at.closingReturnToAgent);
      setReasonOptions(
        Array.isArray(cfg && cfg.outcome_reason_options)
          ? cfg.outcome_reason_options.filter((o) => typeof o === 'string' && o.trim())
          : []
      );
      setNewReasonOption('');
      // Seções avançadas começam colapsadas para manter o foco no fluxo principal.
      setShowAdvanced(false);
      setShowAiAdvanced(
        ad.message_type === 'ai_generated' && (
          ad.use_conversation_context === false || ad.force_response === false
        )
      );
      setError(null);
    };

    if (isEntry) {
      let cancelled = false;
      setEntryLoading(true);
      customFieldsService.getSchema()
        .then((schema) => {
          if (cancelled) return;
          const defs = (schema && (schema.definitions || (schema.data || {}).definitions)) || [];
          setSelectFields(defs.filter((d) => d.type === 'select'));
        })
        .catch(() => { if (!cancelled) setSelectFields([]); });
      // Conexões de entrada: alimentam a origem por número. Falha aqui só
      // esconde essa parte da seção; o resto da automação continua.
      authService.listEntryConnections()
        .then((list) => { if (!cancelled) setEntryConnections(Array.isArray(list) ? list : []); })
        .catch(() => { if (!cancelled) setEntryConnections([]); });
      // Entrada por número: funis para o seletor "Funil › Etapa" e roletas para a
      // entrada e para a resposta a disparo. Falha aqui só esvazia os seletores.
      setEntryConfigLoaded(false);
      authService.listFunnels()
        .then((list) => { if (!cancelled) setFunnelsList(Array.isArray(list) ? list : []); })
        .catch(() => { if (!cancelled) setFunnelsList([]); });
      assignmentService.getTransferRoulettes()
        .then((d) => { if (!cancelled) setRoulettes(Array.isArray(d && d.data) ? d.data : []); })
        .catch(() => { if (!cancelled) setRoulettes([]); });
      authService.getFunnelEntryAutomation(funnel.id)
        .then((res) => {
          if (cancelled) return;
          const cfg = (res && res.data) || res || {};
          applyConfig(cfg);
          setConnectionStageMap(lerMapaDeEntrada(cfg.by_connection_stage));
          setReplyOwnerRouletteId(cfg.reply_owner_roulette_id || '');
          setEntryIsDefault(Boolean(cfg.is_default));
          setEntryConfigLoaded(true);
        })
        .catch((err) => {
          if (!cancelled) { console.warn('getFunnelEntryAutomation failed', err); applyConfig({}); }
        })
        .finally(() => { if (!cancelled) setEntryLoading(false); });
      return () => { cancelled = true; };
    }
    applyConfig(label);
  }, [isOpen, label, isEntry, funnel]);

  // Quando detectarmos as conexoes, ajusta selectedChannel automaticamente
  useEffect(() => {
    if (!isOpen || connections.loading) return;
    if (label?.auto_dispatch?.channel) return; // respeita escolha persistida
    if (connections.business && !connections.qr) setSelectedChannel('wa_business');
    else if (connections.qr && !connections.business) setSelectedChannel('wa_personal');
  }, [isOpen, connections.loading, connections.business, connections.qr, label?.auto_dispatch?.channel]);

  // ============================================
  // Load reference data (followups + labels + plan limits)
  // ============================================
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [fuRes, lblRes, limitsRes, targetsRes, tagsRes, funRes] = await Promise.all([
          followupService.list(null, 'scheduled_message').catch(() => ({ data: [] })),
          authService.getLabels().catch(() => ({ data: [] })),
          followupService.getLimits().catch(() => null),
          assignmentService.getTargets().catch(() => ({ data: [] })),
          contactTagsService.listTags().catch(() => []),
          authService.listFunnels().catch(() => []),
        ]);
        if (cancelled) return;
        const fuList = (fuRes.data || fuRes || [])
          .filter((f) => ['active', 'draft'].includes(f.status));
        setFollowups(fuList);
        setAllLabels(lblRes.data || lblRes || []);
        if (limitsRes) setLimits(limitsRes.data || limitsRes);
        setAssignmentTargets(targetsRes.data || targetsRes || []);
        setAllTags(Array.isArray(tagsRes) ? tagsRes : (tagsRes?.data || []));
        if (Array.isArray(funRes) && funRes.length) setFunnelsList(funRes);
      } catch (err) {
        if (!cancelled) console.warn('LabelConfigDrawer load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Carrega templates Meta quando WA Business e modo IA estao ativos
  useEffect(() => {
    if (!isOpen || !hasBusiness) return;
    let cancelled = false;
    setTemplatesLoading(true);
    templateService.list()
      .then(res => {
        if (cancelled) return;
        const list = res?.templates || res?.data || (Array.isArray(res) ? res : []) || [];
        const byName = new Map();
        list.forEach(t => {
          if (!t?.name) return;
          const existing = byName.get(t.name);
          if (!existing || (existing.status !== 'APPROVED' && t.status === 'APPROVED')) {
            byName.set(t.name, t);
          }
        });
        // Disparo por etiqueta envia template sem components — só template
        // sem variável funciona.
        setAvailableTemplates(
          Array.from(byName.values())
            .filter((t) => !templateRequiresParams(t))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        setAllBusinessTemplates(list);
      })
      .catch(() => { if (!cancelled) setAvailableTemplates([]); })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, hasBusiness]);

  const otherLabels = useMemo(
    () => allLabels.filter((l) => l.id !== label?.id),
    [allLabels, label?.id]
  );

  if (!isOpen) return null;
  if (isEntry ? !funnel : !label) return null;

  // ============================================
  // Rules helpers
  // ============================================
  const updateRule = (idx, patch) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const updateRulePattern = (idx, patternIdx, value) => {
    setRules((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const patterns = r.patterns.slice();
      patterns[patternIdx] = value;
      return { ...r, patterns };
    }));
  };
  const addPattern = (idx) => {
    setRules((prev) => prev.map((r, i) => (
      i === idx ? { ...r, patterns: [...r.patterns, ''] } : r
    )));
  };
  const removePattern = (idx, patternIdx) => {
    setRules((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const patterns = r.patterns.filter((_, p) => p !== patternIdx);
      return { ...r, patterns: patterns.length ? patterns : [''] };
    }));
  };

  // Dedup igual ao backend (minúsculo, sem acento, espaços colapsados): evita
  // "Sem estoque" e "SEM ESTOQUE" virarem dois motivos diferentes.
  const addReasonOption = () => {
    const t = newReasonOption.trim().replace(/\s+/g, ' ').slice(0, 120);
    if (t.length < 2) return;
    const key = (s) => s.normalize('NFD').replace(REASON_DIACRITICS, '').toLowerCase();
    if (reasonOptions.some((o) => key(o) === key(t))) { setNewReasonOption(''); return; }
    if (reasonOptions.length >= 50) return;
    setReasonOptions((p) => [...p, t]);
    setNewReasonOption('');
  };

  // ============================================
  // Save
  // ============================================
  const handleSave = async () => {
    setError(null);
    // Validate rules
    for (const r of rules) {
      if (r.trigger_type !== 'any_message') {
        const cleaned = (r.patterns || []).map(p => p.trim()).filter(Boolean);
        if (cleaned.length === 0) {
          setError('Toda regra (exceto "qualquer mensagem") precisa de ao menos um padrão.');
          return;
        }
      }
      const tgt = ACTION_OPTIONS.find(a => a.value === r.action?.type)?.target;
      if (tgt === 'label' && !r.action?.target_label_id) {
        setError('Ações de etapa precisam de uma etapa-alvo.');
        return;
      }
      if (tgt === 'tags' && !(r.action?.target_tag_ids?.length)) {
        setError('Ações de tag precisam de ao menos uma tag.');
        return;
      }
    }

    // Validate auto_dispatch
    if (autoDispatchEnabled) {
      if (autoDispatchMode === 'inline') {
        if (messageType === 'custom') {
          const hasText = autoDispatchText.trim().length > 0;
          const hasFiles = autoDispatchFiles.length > 0;
          if (!hasText && !hasFiles) {
            setError('Escreva uma mensagem ou anexe um arquivo para o auto-disparo.');
            return;
          }
        } else if (messageType === 'ai_generated') {
          if (instruction.trim().length < 10) {
            setError('Instrução para a IA precisa de pelo menos 10 caracteres.');
            return;
          }
        } else if (messageType === 'template') {
          if (!mainTemplateName.trim()) {
            setError('Escolha um template aprovado da Meta.');
            return;
          }
          if (templateUi) {
            const tpl = findTemplate(uniqueApproved(allBusinessTemplates), mainTemplateName, mainTemplateLanguage);
            const uso = tpl ? templateUsability(analyzeTemplate(tpl), greetingCaps) : null;
            if (uso && !uso.usable) {
              setError(uso.reason);
              return;
            }
          }
          if (selectedChannel !== 'wa_business') {
            setError('Templates Meta só podem ser enviados via WhatsApp Business.');
            return;
          }
        }
        if (selectedChannel === 'wa_business' && messageType !== 'template' && useFallbackTemplate && !fallbackTemplateName.trim()) {
          setError('Escolha um template aprovado ou desative a mensagem padrão.');
          return;
        }
        if (templateUi && selectedChannel === 'wa_business' && messageType !== 'template' && useFallbackTemplate) {
          const tpl = findTemplate(uniqueApproved(allBusinessTemplates), fallbackTemplateName, fallbackTemplateLanguage);
          const uso = tpl ? templateUsability(analyzeTemplate(tpl), fallbackCaps) : null;
          if (uso && !uso.usable) {
            setError(`Mensagem padrão: ${uso.reason}`);
            return;
          }
        }
      } else if (autoDispatchMode === 'advanced') {
        if (!autoDispatchFollowupId) {
          setError('Escolha um follow-up para atrelar.');
          return;
        }
      }
    }

    if (!isEntry && closingUi && closingEnabled && closingReasonTagIds.length > MAX_TAGS_DE_MOTIVO) {
      setError(`Escolha no máximo ${MAX_TAGS_DE_MOTIVO} tags de motivo.`);
      return;
    }

    // Validate distribution
    if (distributionEnabled) {
      if (distributionMode === 'fixed' && !distributionFixedKey) {
        setError('Escolha um destino fixo para a distribuição.');
        return;
      }
      if (distributionMode === 'round_robin' && distributionPoolKeys.size === 0) {
        setError('Selecione ao menos um membro ou agente para o round-robin.');
        return;
      }
    }

    const parseKey = (key) => {
      const i = key.indexOf(':');
      return { type: key.slice(0, i), id: key.slice(i + 1) };
    };
    const distributionPayload = {
      enabled: distributionEnabled,
      mode: distributionMode,
      fixed_target: distributionEnabled && distributionMode === 'fixed' && distributionFixedKey
        ? parseKey(distributionFixedKey) : null,
      pool: distributionEnabled && distributionMode === 'round_robin'
        ? Array.from(distributionPoolKeys).map(parseKey) : [],
    };

    const cleanedRules = rules.map((r) => {
      const tgt = ACTION_OPTIONS.find(a => a.value === r.action.type)?.target;
      return {
        ...(r.id && !r.id.startsWith('tmp_') ? { id: r.id } : {}),
        trigger_type: r.trigger_type,
        patterns: (r.patterns || []).map(p => p.trim()).filter(Boolean),
        case_insensitive: !!r.case_insensitive,
        action: {
          type: r.action.type,
          target_label_id: tgt === 'label' ? r.action.target_label_id : null,
          target_agent_id: tgt === 'agent' ? (r.action.target_agent_id || null) : null,
          target_tag_ids: tgt === 'tags' ? (r.action.target_tag_ids || []) : [],
        },
      };
    });

    setSaving(true);
    try {
      const useInline = autoDispatchEnabled && autoDispatchMode === 'inline';
      const useAdvanced = autoDispatchEnabled && autoDispatchMode === 'advanced';
      const useAi = useInline && messageType === 'ai_generated';
      const useTemplateMain = useInline && messageType === 'template';
      const useCustom = useInline && messageType === 'custom';
      const filesPayload = autoDispatchFiles.map(f => ({
        url: f.url,
        type: f.type,
        filename: f.filename,
        mimeType: f.mimeType,
      }));
      // Template Meta forca canal wa_business no backend; aqui replicamos.
      const effectiveChannel = useTemplateMain ? 'wa_business' : selectedChannel;

      const payload = {
        auto_dispatch: {
          enabled: autoDispatchEnabled,
          followup_id: useAdvanced ? autoDispatchFollowupId : null,
          // inline custom
          text: useCustom ? (autoDispatchText.trim() || null) : null,
          files: useCustom ? filesPayload : [],
          // tipo da mensagem inline (custom | ai_generated | template)
          message_type: useInline ? messageType : 'custom',
          // inline ai_generated
          instruction: useAi ? instruction.trim() : null,
          use_conversation_context: useAi ? useConversationContext : true,
          force_response: useAi ? forceResponse : true,
          // inline template (mensagem principal)
          template_name: useTemplateMain ? mainTemplateName.trim() : null,
          template_language: useTemplateMain ? (mainTemplateLanguage || 'pt_BR') : null,
          // fallback template (WA Business, modos custom e ai_generated)
          fallback_template_name: useInline && !useTemplateMain && effectiveChannel === 'wa_business' && useFallbackTemplate
            ? (fallbackTemplateName || null)
            : null,
          fallback_template_language: fallbackTemplateLanguage,
          channel: useInline ? effectiveChannel : null,
          // shared
          anti_block: autoDispatchEnabled && effectiveChannel === 'wa_personal' && autoDispatchAntiBlock,
          typing_simulation: autoDispatchEnabled && effectiveChannel === 'wa_personal' && autoDispatchTyping,
          repeat_on_reentry: autoDispatchEnabled && autoDispatchRepeat,
        },
        rules: cleanedRules,
        distribution: distributionPayload,
        entry_tags: { add: entryTagsAdd, remove: entryTagsRemove },
        // Origem do lead: só na entrada do funil. Objeto vazio limpa.
        ...(isEntry ? {
          lead_source: {
            field_id: leadSourceFieldId || null,
            by_signal: Object.fromEntries(Object.entries(leadSourceBySignal).filter(([, v]) => v)),
            by_phrase: leadSourcePhrases.filter((p) => (p.phrase || '').trim() && p.option_id),
            by_connection: Object.fromEntries(
              Object.entries(leadSourceByConnection).filter(([, v]) => v)
            ),
            default: leadSourceDefault || null,
          },
        } : {}),
        // Entrada por número e roleta da resposta a disparo: só quando a seção
        // apareceu. Campo ausente = o backend mantém o que estava salvo.
        ...(showEntryByConnection ? { by_connection_stage: mapaParaSalvar(connectionStageMap) } : {}),
        ...(showReplyOwner ? { reply_owner_roulette_id: replyOwnerRouletteId || '' } : {}),
      };

      // 'open' limpa a marcação no backend (null no payload significaria "não
      // alterar"). Entrada de funil não tem resultado — fica fora do payload.
      const res = isEntry
        ? await authService.setFunnelEntryAutomation(funnel.id, payload)
        : await authService.updateLabel(label.id, {
            ...payload,
            outcome: outcome || 'open',
            // Etapa aberta não tem motivo de fechamento: limpa a lista para
            // não deixar dado órfão quando o cliente desmarca o resultado.
            outcome_reason_options: outcome ? reasonOptions : [],
            // Copiar para outro funil: só com a flag (ausente = não alterar).
            ...(multiFunnel ? {
              funnel_copy: {
                enabled: funnelCopyEnabled && Boolean(funnelCopyLabelId),
                label_id: funnelCopyLabelId || null,
                only_if_empty: funnelCopyOnlyIfEmpty,
              },
            } : {}),
            // Etapa por mensagem e encerramento: só com as flags (ausente =
            // não alterar). Etapa de encerramento não muda quando nós
            // respondemos: a seção desabilita e o payload limpa.
            ...atendimentoParaSalvar({
              transitionOnInbound, transitionOnOutbound, closingEnabled,
              closingSummary, closingReasonTagIds, closingReturnToAgent,
            }, { transitionsUi: messageTransitionsUi, closingUi }),
          });
      const updated = res?.data || res;
      if (onSaved) onSaved(updated);
      onClose();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || err.message || 'Erro ao salvar';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // Reusable step style + choice card
  // ============================================
  const stepStyles = {
    section: { display: 'flex', flexDirection: 'column', gap: 6 },
    head: { display: 'flex', alignItems: 'center', gap: 8 },
    num: {
      width: 22, height: 22, borderRadius: '50%',
      backgroundColor: colors.accentPrimary, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, flexShrink: 0,
    },
    title: {
      margin: 0, fontSize: 14, fontWeight: 600, color: colors.textPrimary,
    },
    hint: {
      margin: '0 0 4px 30px', fontSize: 12, color: colors.textSecondary, lineHeight: 1.45,
    },
    body: { marginLeft: 30, display: 'flex', flexDirection: 'column', gap: 10 },
  };

  const choiceCard = ({ icon: Icon, label: l, desc, selected, locked, onClick }) => (
    <button
      type="button"
      onClick={locked ? undefined : onClick}
      disabled={locked}
      style={{
        flex: 1, textAlign: 'left',
        padding: '12px 14px', borderRadius: 10,
        backgroundColor: selected ? `${colors.accentPrimary}10` : colors.bgSecondary,
        border: `1.5px solid ${selected ? colors.accentPrimary : colors.borderColor}`,
        cursor: locked ? 'not-allowed' : 'pointer',
        display: 'flex', gap: 10, alignItems: 'flex-start',
        transition: 'all 0.15s', position: 'relative',
        opacity: locked ? 0.7 : 1,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        backgroundColor: selected ? colors.accentPrimary : `${colors.accentPrimary}15`,
        color: selected ? '#fff' : colors.accentPrimary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: selected ? colors.accentPrimary : colors.textPrimary,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {locked && <Lock size={11} />}
          {l}
        </div>
        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.35 }}>
          {desc}
        </div>
      </div>
      {locked && (
        <span style={{
          position: 'absolute', top: 6, right: 8,
          backgroundColor: colors.accentPrimary, color: '#fff',
          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
        }}>PRO</span>
      )}
    </button>
  );

  const isAi = messageType === 'ai_generated';
  const showAntiBlockOptions = selectedChannel === 'wa_personal';
  // Fallback so faz sentido em modos custom/ai_generated (com Template como
  // principal seria redundante — o backend ja bloqueia essa combinacao).
  const showBusinessFallback = selectedChannel === 'wa_business' && hasBusiness && messageType !== 'template';

  // Alvos de distribuição agrupados (Equipe + Agentes de IA).
  const groupedTargets = (() => {
    const members = assignmentTargets.filter((t) => t.type === 'member');
    const agents = assignmentTargets.filter((t) => t.type === 'agent');
    const out = [];
    if (members.length) out.push({ key: 'members', title: 'Equipe', items: members });
    if (agents.length) out.push({ key: 'agents', title: 'Agentes de IA', items: agents });
    return out;
  })();

  const agentTargets = assignmentTargets.filter((t) => t.type === 'agent');

  // Seletor compacto de tags (só as escolhidas viram chip; o catálogo fica
  // atrás de busca + lista). Antes era a parede com o catálogo inteiro, duas
  // vezes seguidas. Usado nas tags de entrada e na ação de regra antiga.
  const renderTagPicker = (selectedIds, onChangeIds, noneText) => (
    <ChipMultiSelect
      options={allTags}
      value={selectedIds || []}
      onChange={onChangeIds}
      colors={colors}
      addLabel="Adicionar tag"
      searchPlaceholder="Buscar tag..."
      emptyText="Nenhuma tag criada ainda. Crie em Contatos."
      noneText={noneText}
    />
  );

  const renderTargetRow = (t, selected, onClick, isCheckbox) => (
    <button
      key={`${t.type}:${t.id}`}
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '9px 12px', borderRadius: 8, textAlign: 'left',
        backgroundColor: selected ? `${colors.accentPrimary}10` : 'transparent',
        border: `1px solid ${selected ? colors.accentPrimary : colors.borderColor}`,
        cursor: 'pointer', transition: 'background-color 0.12s, border-color 0.12s',
      }}
    >
      <span style={{
        width: 16, height: 16, flexShrink: 0,
        borderRadius: isCheckbox ? 4 : '50%',
        border: `2px solid ${selected ? colors.accentPrimary : colors.textTertiary}`,
        backgroundColor: selected ? colors.accentPrimary : 'transparent',
      }} />
      <span style={{
        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: `${colors.accentPrimary}18`, color: colors.accentPrimary,
      }}>
        {t.type === 'agent' ? <Bot size={13} /> : <User size={13} />}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: 13, color: colors.textPrimary }}>{t.name}</span>
        {(t.role || t.category) && (
          <span style={{ fontSize: 10.5, color: colors.textTertiary }}>
            {[
              t.role ? (t.role === 'admin' ? 'Administrador' : 'Membro') : null,
              t.category?.title || null,
            ].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
    </button>
  );

  // Seção: Distribuir o contato (responsável fixo ou round-robin entre
  // membros/agentes). Independente do auto-disparo.
  const renderDistribution = () => (
    <section style={stepStyles.section}>
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
        padding: 12, borderRadius: 10,
        backgroundColor: distributionEnabled ? `${colors.accentPrimary}08` : colors.bgSecondary,
        border: `1px solid ${distributionEnabled ? colors.accentPrimary : colors.borderColor}`,
        transition: 'all 0.15s',
      }}>
        <input
          type="checkbox"
          checked={distributionEnabled}
          onChange={(e) => setDistributionEnabled(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Shuffle size={14} color={colors.accentPrimary} />
            <strong style={{ fontSize: 13, color: colors.textPrimary }}>
              Distribuir o contato para um responsável
            </strong>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textSecondary }}>
            {isEntry
              ? 'Ao chegar um contato novo (sem etapa e sem responsável), atribui automaticamente a um membro ou agente de IA.'
              : 'Ao entrar nesta etapa, atribui o contato a um membro ou agente de IA.'}
          </p>
        </div>
      </label>

      {distributionEnabled && (
        <div style={{ ...stepStyles.body, marginLeft: 0, marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {choiceCard({
              icon: Target,
              label: 'Destino fixo',
              desc: 'Todo contato vai para o mesmo membro ou agente.',
              selected: distributionMode === 'fixed',
              onClick: () => setDistributionMode('fixed'),
            })}
            {choiceCard({
              icon: Shuffle,
              label: 'Round-robin',
              desc: 'Reveza entre os selecionados, um por vez.',
              selected: distributionMode === 'round_robin',
              onClick: () => setDistributionMode('round_robin'),
            })}
          </div>

          {groupedTargets.length === 0 ? (
            <p style={{ margin: 0, fontSize: 12, color: colors.textTertiary }}>
              Nenhum membro ou agente disponível para distribuir.
            </p>
          ) : groupedTargets.map(({ key, title, items }) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: colors.textTertiary,
                textTransform: 'uppercase', letterSpacing: '0.4px',
              }}>{title}</span>
              {items.map((t) => {
                const k = `${t.type}:${t.id}`;
                if (distributionMode === 'fixed') {
                  return renderTargetRow(t, distributionFixedKey === k, () => setDistributionFixedKey(k), false);
                }
                const selected = distributionPoolKeys.has(k);
                return renderTargetRow(t, selected, () => {
                  setDistributionPoolKeys((prev) => {
                    const next = new Set(prev);
                    if (next.has(k)) next.delete(k); else next.add(k);
                    return next;
                  });
                }, true);
              })}
            </div>
          ))}
        </div>
      )}
    </section>
  );

  // Seção: Tags ao entrar (aplicar/remover incondicionalmente ao chegar no
  // funil / na etiqueta) — independe de regra.
  const renderEntryTags = () => (
    <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 4,
      }}>
        <Tag size={14} color={colors.accentPrimary} />
        Tags ao entrar
      </div>
      <p style={{ margin: 0, fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>
        {isEntry
          ? 'Aplica ou remove tags automaticamente quando um contato novo chega no funil.'
          : 'Aplica ou remove tags automaticamente quando o contato entra nesta etapa.'}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 6 }}>Aplicar tags</div>
          {renderTagPicker(entryTagsAdd, setEntryTagsAdd, 'Nenhuma tag aplicada.')}
        </div>
        <div>
          <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 6 }}>Remover tags</div>
          {renderTagPicker(entryTagsRemove, setEntryTagsRemove, 'Nenhuma tag removida.')}
        </div>
      </div>
    </section>
  );

  // Seção: Mudar de etapa sozinho (FUNNEL_MESSAGE_TRANSITIONS_ENABLED). A cada
  // mensagem, em qualquer canal, o contato que está NESTA etapa vai para a
  // etapa escolhida do mesmo funil. Etapa de encerramento não muda quando nós
  // respondemos: o select fica desabilitado com o motivo.
  const renderMessageTransitions = () => {
    const etapas = etapasDoMesmoFunil(allLabels, label);
    const saidaTravada = saidaBloqueada(closingUi, closingEnabled);
    const selectEtapa = (value, onChange, disabled) => (
      <select
        value={disabled ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          padding: '9px 10px', borderRadius: 8, fontSize: 13,
          border: `1px solid ${colors.borderColor}`,
          backgroundColor: disabled ? colors.bgSecondary : colors.bgPrimary,
          color: disabled ? colors.textTertiary : colors.textPrimary,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <option value="">Fica nesta etapa</option>
        {etapas.map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </select>
    );
    const configurada = Boolean(transitionOnInbound || (!saidaTravada && transitionOnOutbound));
    return (
      <section style={{
        ...stepStyles.section, padding: 12, borderRadius: 10,
        backgroundColor: configurada ? `${colors.accentPrimary}08` : colors.bgSecondary,
        border: `1px solid ${configurada ? colors.accentPrimary : colors.borderColor}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ArrowLeftRight size={14} color={colors.accentPrimary} />
          <strong style={{ fontSize: 13, color: colors.textPrimary }}>
            Mudar de etapa sozinho
          </strong>
        </div>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textSecondary }}>
          A cada mensagem da conversa, em qualquer canal, o contato que está nesta etapa
          vai para a etapa escolhida. Só vale para quem está nesta etapa.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary }}>
              Quando o cliente escrever, mover para
            </span>
            {selectEtapa(transitionOnInbound, setTransitionOnInbound, false)}
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary }}>
              Quando nós respondermos (agente, equipe ou automação), mover para
            </span>
            {selectEtapa(transitionOnOutbound, setTransitionOnOutbound, saidaTravada)}
            {saidaTravada && (
              <span style={{ fontSize: 11, color: colors.textTertiary, lineHeight: 1.4 }}>
                Esta etapa encerra o atendimento: ela não muda quando nós respondemos,
                senão a própria mensagem de encerramento puxaria o contato de volta.
              </span>
            )}
          </label>
          {etapas.length === 0 && (
            <span style={{ fontSize: 12, color: colors.textTertiary }}>
              Crie outras etapas neste funil para poder escolher o destino.
            </span>
          )}
        </div>
      </section>
    );
  };

  // Seção: Encerramento do atendimento (FUNNEL_STAGE_CLOSING_ENABLED). Ao
  // entrar aqui, por qualquer caminho, o atendimento fica registrado (resumo
  // e tag de motivo) e o contato volta para o agente.
  const renderClosing = () => (
    <section style={stepStyles.section}>
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
        padding: 12, borderRadius: 10,
        backgroundColor: closingEnabled ? `${colors.accentPrimary}08` : colors.bgSecondary,
        border: `1px solid ${closingEnabled ? colors.accentPrimary : colors.borderColor}`,
        transition: 'all 0.15s',
      }}>
        <input
          type="checkbox"
          checked={closingEnabled}
          onChange={(e) => setClosingEnabled(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Flag size={14} color={colors.accentPrimary} />
            <strong style={{ fontSize: 13, color: colors.textPrimary }}>
              Esta etapa encerra o atendimento
            </strong>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textSecondary }}>
            Quando o contato entra aqui, por qualquer caminho (régua, arrastar, agente), o
            atendimento fica registrado. Um contato pode ter vários atendimentos: cada um
            ganha o seu registro.
          </p>
        </div>
      </label>

      {closingEnabled && (
        <div style={{ ...stepStyles.body, marginLeft: 0, marginTop: 10 }}>
          <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
            <input
              type="checkbox"
              checked={closingSummary}
              onChange={(e) => setClosingSummary(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              <strong>Registrar o resumo da IA nos comentários</strong>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                O agente lê a conversa (inclusive o que a equipe falou) e escreve o que o
                cliente queria, o que foi feito e o que ficou pendente.
              </div>
            </span>
          </label>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>
              Tag de motivo
            </div>
            <p style={{ margin: '2px 0 8px', fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>
              A IA escolhe uma destas tags pelo assunto do atendimento. É uma lista fixa:
              crie as tags antes, em Contatos; o encerramento nunca cria tag nova.
            </p>
            {renderTagPicker(closingReasonTagIds, setClosingReasonTagIds, 'Sem tag de motivo.')}
          </div>
          <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
            <input
              type="checkbox"
              checked={closingReturnToAgent}
              onChange={(e) => setClosingReturnToAgent(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              <strong>Devolver o contato ao agente</strong>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                Se alguém da equipe atendeu, o contato volta para o agente sem mandar
                mensagem: o próximo atendimento é da IA e da régua. Contato pausado
                continua pausado.
              </div>
            </span>
          </label>
        </div>
      )}
    </section>
  );

  // Seção: Origem do lead (entrada do funil). Decide a Fonte de forma
  // determinística, ANTES do agente: sinal da mensageria (anúncio, link),
  // frase padrão da Meta na 1ª mensagem, ou padrão. Antes só existia via
  // script — era "funcionalidade sem tela".
  const SIGNAL_KEYS = [
    ['ctwa_ad', 'Anúncio click-to-WhatsApp (qualquer app)'],
    ['ctwa_ad:facebook', 'Anúncio click-to-WhatsApp vindo do Facebook'],
    ['ctwa_ad:instagram', 'Anúncio click-to-WhatsApp vindo do Instagram'],
    ['ctwa_post', 'Post com botão de WhatsApp'],
    ['click_to_chat_link', 'Link wa.me / click-to-chat'],
    ['shortlink', 'Link curto da Página (Messenger/Instagram)'],
    ['customer_chat_plugin', 'Plugin de chat do site (Messenger)'],
  ];
  const META_PHRASES = [
    'Olá! Posso ter mais informações sobre isso?',
    'Tenho interesse e queria mais informações',
    'Olá! Me interesso em comprar o veículo',
    'Quero mais informações para vender meu veículo',
  ];
  const inputStyle = {
    padding: '7px 10px', borderRadius: 8, fontSize: 12,
    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
    color: colors.textPrimary, outline: 'none',
  };
  const leadSourceField = selectFields.find((f) => f.id === leadSourceFieldId) || null;
  const leadSourceOptions = (leadSourceField && leadSourceField.options) || [];
  const renderOptionSelect = (value, onChange, placeholder = 'Não definir') => (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={!leadSourceField}
      style={{ ...inputStyle, minWidth: 160, flex: 1 }}
    >
      <option value="">{placeholder}</option>
      {leadSourceOptions.map((o) => (
        <option key={o.id} value={o.id}>{o.label}</option>
      ))}
    </select>
  );
  const renderLeadSource = () => (
    <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 4,
      }}>
        <Filter size={14} color={colors.accentPrimary} />
        Origem do lead
      </div>
      <p style={{ margin: 0, fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>
        Preenche a Fonte do lead sozinha, antes de o agente responder: pelo sinal que a
        mensageria entrega (anúncio, link) ou pela primeira frase do contato. A primeira
        origem gravada vence; o agente só preenche o que ficar vazio.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 6 }}>Campo da origem</div>
          <select
            value={leadSourceFieldId}
            onChange={(e) => { setLeadSourceFieldId(e.target.value); setLeadSourceBySignal({}); setLeadSourcePhrases([]); setLeadSourceByConnection({}); setLeadSourceDefault(''); }}
            style={{ ...inputStyle, width: '100%' }}
          >
            <option value="">Escolha o campo de lista (papel "Fonte do lead")</option>
            {selectFields.map((f) => (
              <option key={f.id} value={f.id}>{f.name}{f.role === 'lead_source' ? ' · Fonte do lead' : ''}</option>
            ))}
          </select>
          {selectFields.length === 0 && (
            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>
              Crie um campo personalizado do tipo lista em Contatos › Campos e marque o papel "Fonte do lead".
            </div>
          )}
        </div>
        {leadSourceField && (
          <>
            <div>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 6 }}>
                Pelo sinal de entrada (o que a Meta declara na mensagem)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {SIGNAL_KEYS.map(([key, label]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: colors.textPrimary, flex: 1.4 }}>{label}</span>
                    {renderOptionSelect(leadSourceBySignal[key], (v) => setLeadSourceBySignal((prev) => {
                      const next = { ...prev };
                      if (v) next[key] = v; else delete next[key];
                      return next;
                    }))}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: colors.textSecondary }}>
                  Pela primeira frase do contato (quando o sinal não vem)
                </span>
                <button
                  type="button"
                  onClick={() => setLeadSourcePhrases((prev) => {
                    const have = new Set(prev.map((p) => (p.phrase || '').trim().toLowerCase()));
                    const extra = META_PHRASES.filter((f) => !have.has(f.toLowerCase())).map((f) => ({ phrase: f, option_id: '' }));
                    return [...prev, ...extra];
                  })}
                  style={{ background: 'transparent', border: `1px solid ${colors.borderColor}`, borderRadius: 6, padding: '3px 8px', fontSize: 11, color: colors.accentPrimary, cursor: 'pointer' }}
                >
                  Usar frases padrão da Meta
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {leadSourcePhrases.map((p, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="text"
                      value={p.phrase}
                      placeholder="Frase exata que o contato manda"
                      onChange={(e) => setLeadSourcePhrases((prev) => prev.map((x, i) => (i === idx ? { ...x, phrase: e.target.value } : x)))}
                      style={{ ...inputStyle, flex: 1.6 }}
                    />
                    {renderOptionSelect(p.option_id, (v) => setLeadSourcePhrases((prev) => prev.map((x, i) => (i === idx ? { ...x, option_id: v } : x))), 'Opção...')}
                    <button
                      type="button"
                      onClick={() => setLeadSourcePhrases((prev) => prev.filter((_, i) => i !== idx))}
                      title="Remover"
                      style={{ background: 'transparent', border: 'none', color: colors.textTertiary, cursor: 'pointer', fontSize: 14 }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setLeadSourcePhrases((prev) => [...prev, { phrase: '', option_id: '' }])}
                  style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', padding: 0, fontSize: 11, color: colors.accentPrimary, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  + adicionar frase
                </button>
              </div>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>
                A frase casa por igualdade ou como início da mensagem (ex.: frase + nome do veículo), ignorando acentos, maiúsculas e pontuação. Só na primeira mensagem do contato.
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 6 }}>
                Pelo número por onde o lead chegou (quando nem o sinal nem a frase casam)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {entryConnections.map((c) => (
                  <div key={c.ref} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: colors.textPrimary, flex: 1.4 }}>
                      {c.label}
                      {(c.phone || CANAL_ROTULO[c.channel]) && (
                        <span style={{ color: colors.textTertiary, fontSize: 11 }}>
                          {' · '}{c.phone || CANAL_ROTULO[c.channel]}
                        </span>
                      )}
                    </span>
                    {renderOptionSelect(leadSourceByConnection[c.ref], (v) => setLeadSourceByConnection((prev) => {
                      const next = { ...prev };
                      if (v) next[c.ref] = v; else delete next[c.ref];
                      return next;
                    }))}
                  </div>
                ))}
                {entryConnections.length === 0 && (
                  <div style={{ fontSize: 11, color: colors.textTertiary }}>
                    Nenhuma conexão encontrada neste workspace.
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>
                Serve para separar o lead que caiu no número de um consultor (prospecção dele) do
                que chegou pelo número geral. A lista traz as conexões dos quatro canais e vale só
                quando a Fonte ainda está vazia.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: colors.textPrimary, flex: 1.4 }}>Padrão quando nada casa</span>
              {renderOptionSelect(leadSourceDefault, (v) => setLeadSourceDefault(v))}
            </div>
          </>
        )}
      </div>
    </section>
  );

  // Seção: Entrada por número (só funil padrão, flag entry_by_connection_stage).
  // Contato novo que chega por uma conexão cai direto numa etapa de qualquer
  // funil, com roleta opcional, no lugar da entrada padrão. A lista de conexões
  // é a mesma da Origem do lead (os quatro canais).
  const opcoesEtapa = opcoesDeEtapa(funnelsList, allLabels);
  const setConnectionStage = (ref, patch) => setConnectionStageMap((prev) => {
    const next = { ...prev };
    const item = { label_id: '', roulette_id: '', ...(prev[ref] || {}), ...patch };
    if (item.label_id) next[ref] = item; else delete next[ref];
    return next;
  });
  const renderRouletteOptions = (value) => (
    <>
      {roulettes.map((g) => (
        <option key={g.id} value={g.id}>{g.name}</option>
      ))}
      {value && !roulettes.some((g) => g.id === value) && (
        <option value={value}>Roleta não encontrada</option>
      )}
    </>
  );
  const renderEntryByConnection = () => (
    <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 4,
      }}>
        <Link2 size={14} color={colors.accentPrimary} />
        Entrada por número
      </div>
      <p style={{ margin: 0, fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>
        Contato novo que chega pela conexão cai direto na etapa escolhida, no lugar da entrada padrão.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {linhasDeConexao(entryConnections, connectionStageMap).map((c) => {
          const item = connectionStageMap[c.ref] || { label_id: '', roulette_id: '' };
          return (
            <div key={c.ref} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: colors.textPrimary, flex: '1 1 160px', minWidth: 0 }}>
                {c.label}
                {(c.phone || CANAL_ROTULO[c.channel]) && (
                  <span style={{ color: colors.textTertiary, fontSize: 11 }}>
                    {' · '}{c.phone || CANAL_ROTULO[c.channel]}
                  </span>
                )}
              </span>
              <select
                value={item.label_id}
                onChange={(e) => setConnectionStage(c.ref, { label_id: e.target.value })}
                style={{ ...inputStyle, flex: '1 1 180px', minWidth: 0 }}
              >
                <option value="">Entrada padrão</option>
                {opcoesComSalva(opcoesEtapa, item.label_id).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {item.label_id && (
                <select
                  value={item.roulette_id}
                  onChange={(e) => setConnectionStage(c.ref, { roulette_id: e.target.value })}
                  style={{ ...inputStyle, flex: '1 1 140px', minWidth: 0 }}
                >
                  <option value="">Sem roleta</option>
                  {renderRouletteOptions(item.roulette_id)}
                </select>
              )}
            </div>
          );
        })}
        {entryConnections.length === 0 && Object.keys(connectionStageMap).length === 0 && (
          <div style={{ fontSize: 11, color: colors.textTertiary }}>
            Nenhuma conexão encontrada neste workspace.
          </div>
        )}
      </div>
    </section>
  );

  // Seção: quem assume a resposta a um disparo cujo remetente não é da equipe
  // (roleta de transferência). Só funil padrão; o motor é o da guarda de reentrada.
  const renderReplyOwner = () => (
    <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 4,
      }}>
        <Shuffle size={14} color={colors.accentPrimary} />
        Quem assume resposta a disparo sem remetente
      </div>
      <p style={{ margin: 0, fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>
        Vale para contato sem responsável. Se o disparo saiu de um membro, ele mesmo assume.
      </p>
      <select
        value={replyOwnerRouletteId}
        onChange={(e) => setReplyOwnerRouletteId(e.target.value)}
        style={{ ...inputStyle, width: '100%', marginTop: 10 }}
      >
        <option value="">Ninguém</option>
        {renderRouletteOptions(replyOwnerRouletteId)}
      </select>
      {roulettes.length === 0 && (
        <div style={{ fontSize: 11, color: colors.textTertiary, marginTop: 4 }}>
          Crie uma roleta em Configurar funil › Roleta de transferência.
        </div>
      )}
    </section>
  );

  // Seção: Regras de aproximação. Reagem a mensagens recebidas; a primeira
  // regra que casa executa a ação. Em entrada, vale para o contato sem etiqueta.
  const renderRules = () => (
    <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 4,
      }}>
        <Filter size={14} color={colors.accentPrimary} />
        Regras de aproximação
        {rules.length > 0 && (
          <span style={{
            fontSize: 11, color: colors.textSecondary,
            padding: '2px 8px', borderRadius: 10, backgroundColor: colors.bgSecondary,
          }}>{rules.length}</span>
        )}
      </div>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 4,
        padding: '8px 10px', borderRadius: 8,
        border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
        fontSize: 11, color: colors.textSecondary, lineHeight: 1.4,
      }}>
        <AlertCircle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          Regra antiga: continua funcionando, mas não pode mais ser editada nem criada.
          Para separar leads pela mensagem de chegada, use a Origem do lead e as automações de entrada.
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
        {rules.map((rule, idx) => {
          const actionDef = ACTION_OPTIONS.find(a => a.value === rule.action.type);
          return (
            <div
              key={rule.id}
              style={{
                border: `1px solid ${colors.borderColor}`, borderRadius: 10,
                padding: 12, backgroundColor: colors.bgSecondary,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}>
                  Regra #{idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => setRules(rules.filter((_, i) => i !== idx))}
                  style={{
                    background: 'transparent', border: 'none', color: '#ef4444',
                    cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex',
                    alignItems: 'center', gap: 4, fontSize: 11,
                  }}
                  title="Remover regra"
                >
                  <Trash2 size={14} /> Remover regra
                </button>
              </div>

              {/* Só leitura: a regra antiga não é mais editável. */}
              <fieldset disabled style={{
                border: 'none', padding: 0, margin: 0, minWidth: 0,
                display: 'flex', flexDirection: 'column', gap: 10, opacity: 0.75,
              }}>

              <div>
                <label style={{ display: 'block', fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>
                  Quando
                </label>
                <select
                  value={rule.trigger_type}
                  onChange={(e) => updateRule(idx, { trigger_type: e.target.value })}
                  style={{
                    width: '100%', padding: '6px 8px', borderRadius: 6,
                    border: `1px solid ${colors.borderColor}`,
                    backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: 12, outline: 'none',
                  }}
                >
                  {TRIGGER_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {rule.trigger_type !== 'any_message' && (
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>
                    {rule.trigger_type === 'regex' ? 'Padrões (regex)' : 'Termos'}
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(rule.patterns || []).map((p, pIdx) => (
                      <div key={pIdx} style={{ display: 'flex', gap: 6 }}>
                        <input
                          type="text"
                          value={p}
                          onChange={(e) => updateRulePattern(idx, pIdx, e.target.value)}
                          placeholder={
                            rule.trigger_type === 'regex'
                              ? 'ex: ^(quero|gostaria) (de )?me inscrever'
                              : 'ex: quero me inscrever'
                          }
                          style={{
                            flex: 1, padding: '6px 8px', borderRadius: 6,
                            border: `1px solid ${colors.borderColor}`,
                            backgroundColor: colors.bgPrimary,
                            color: colors.textPrimary, fontSize: 12, outline: 'none',
                            fontFamily: rule.trigger_type === 'regex' ? 'monospace' : 'inherit',
                          }}
                        />
                        {(rule.patterns || []).length > 1 && (
                          <button
                            onClick={() => removePattern(idx, pIdx)}
                            style={{
                              background: 'transparent', border: 'none', color: colors.textTertiary,
                              cursor: 'pointer', padding: 4, display: 'flex',
                            }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => addPattern(idx)}
                      style={{
                        alignSelf: 'flex-start', background: 'transparent',
                        border: 'none', color: colors.accentPrimary, fontSize: 11,
                        cursor: 'pointer', padding: '2px 0', display: 'flex',
                        alignItems: 'center', gap: 4,
                      }}
                    >
                      <Plus size={11} /> Adicionar termo
                    </button>
                  </div>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
                    fontSize: 11, color: colors.textSecondary,
                  }}>
                    <input
                      type="checkbox"
                      checked={!!rule.case_insensitive}
                      onChange={(e) => updateRule(idx, { case_insensitive: e.target.checked })}
                    />
                    Ignorar maiúsculas/minúsculas
                  </label>
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>
                  Então
                </label>
                <select
                  value={rule.action.type}
                  onChange={(e) => updateRule(idx, {
                    action: { ...rule.action, type: e.target.value },
                  })}
                  style={{
                    width: '100%', padding: '6px 8px', borderRadius: 6,
                    border: `1px solid ${colors.borderColor}`,
                    backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: 12, outline: 'none',
                    marginBottom: actionDef?.target ? 6 : 0,
                  }}
                >
                  {ACTION_OPTIONS.map(a => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
                {actionDef?.target === 'label' && (
                  <select
                    value={rule.action.target_label_id || ''}
                    onChange={(e) => updateRule(idx, {
                      action: { ...rule.action, target_label_id: e.target.value || null },
                    })}
                    style={{
                      width: '100%', padding: '6px 8px', borderRadius: 6,
                      border: `1px solid ${colors.borderColor}`,
                      backgroundColor: colors.bgPrimary,
                      color: colors.textPrimary, fontSize: 12, outline: 'none',
                    }}
                  >
                    <option value="">— Etapa-alvo —</option>
                    {otherLabels.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                )}
                {actionDef?.target === 'agent' && (
                  <select
                    value={rule.action.target_agent_id || ''}
                    onChange={(e) => updateRule(idx, {
                      action: { ...rule.action, target_agent_id: e.target.value || null },
                    })}
                    style={{
                      width: '100%', padding: '6px 8px', borderRadius: 6,
                      border: `1px solid ${colors.borderColor}`,
                      backgroundColor: colors.bgPrimary,
                      color: colors.textPrimary, fontSize: 12, outline: 'none',
                    }}
                  >
                    <option value="">— Manter roteamento padrão —</option>
                    {agentTargets.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                )}
                {actionDef?.target === 'tags' && renderTagPicker(
                    rule.action.target_tag_ids || [],
                    (next) => updateRule(idx, { action: { ...rule.action, target_tag_ids: next } }),
                    'Nenhuma tag escolhida.',
                  )}
              </div>
              </fieldset>
            </div>
          );
        })}

        {rules.length === 0 && (
          <div style={{ fontSize: 11, color: colors.textTertiary }}>
            Regras removidas. Salve para confirmar.
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <div style={{
        width: 720, maxWidth: '100%', height: '100%',
        backgroundColor: colors.bgPrimary,
        boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: `1px solid ${colors.borderColor}`,
          backgroundColor: colors.headerBg,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {isEntry
                ? <Inbox size={16} color={colors.accentPrimary} />
                : <Settings size={16} color={colors.accentPrimary} />}
              {isEntry ? 'Automação de entrada' : 'Automações da etapa'}
            </h3>
            <p style={{
              margin: '2px 0 0', fontSize: 12, color: colors.textSecondary,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {isEntry ? (
                <span>Novos contatos no funil <strong>{funnel?.name}</strong> (sem etapa)</span>
              ) : (
                <>
                  <span style={{
                    width: 10, height: 10, borderRadius: 3,
                    backgroundColor: label.color, display: 'inline-block',
                  }} />
                  {label.name}
                </>
              )}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: colors.textSecondary, padding: 6, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Resultado da etapa — só para etiqueta (a entrada não é coluna) */}
          {!isEntry && (
            <section style={stepStyles.section}>
              <div style={stepStyles.head}>
                <h4 style={stepStyles.title}>Resultado da etapa</h4>
              </div>
              <p style={{ margin: '0 0 4px', fontSize: 12, color: colors.textSecondary }}>
                Marque se cair aqui significa fechar o funil. Vale para os
                relatórios, independente do nome da coluna. Cada funil tem no
                máximo uma etapa de ganho e uma de perda.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { value: null, icon: CircleDashed, label: 'Em aberto', tint: colors.textSecondary },
                  { value: 'won', icon: Trophy, label: 'Ganho', tint: colors.accentPrimary },
                  { value: 'lost', icon: XCircle, label: 'Perda', tint: colors.danger },
                ].map(({ value, icon: Icon, label: optLabel, tint }) => {
                  const selected = outcome === value;
                  return (
                    <button
                      key={optLabel}
                      type="button"
                      onClick={() => setOutcome(value)}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 10,
                        backgroundColor: selected ? `${tint}12` : colors.bgSecondary,
                        border: `1.5px solid ${selected ? tint : colors.borderColor}`,
                        color: selected ? tint : colors.textPrimary,
                        cursor: 'pointer', transition: 'all 0.15s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 7, fontSize: 13, fontWeight: 600,
                      }}
                    >
                      <Icon size={15} />
                      {optLabel}
                    </button>
                  );
                })}
              </div>

              {/* Motivos sugeridos — só fazem sentido em etapa de fechamento.
                  Lista vazia mantém o modal apenas com texto livre. */}
              {outcome && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                    Motivos sugeridos
                  </div>
                  <p style={{ margin: '3px 0 8px', fontSize: 12, color: colors.textSecondary }}>
                    Viram atalhos no momento do fechamento. Deixe vazio para pedir
                    só texto livre. Um motivo novo digitado entra aqui sozinho.
                  </p>

                  {reasonOptions.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                      {reasonOptions.map((opt) => (
                        <span
                          key={opt}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '4px 8px', borderRadius: 999, fontSize: 12,
                            backgroundColor: colors.bgSecondary,
                            border: `1px solid ${colors.borderColor}`,
                            color: colors.textPrimary, maxWidth: '100%',
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {opt}
                          </span>
                          <button
                            type="button"
                            onClick={() => setReasonOptions((p) => p.filter((x) => x !== opt))}
                            title="Remover motivo"
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: 0, display: 'flex', color: colors.textTertiary,
                            }}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      value={newReasonOption}
                      maxLength={120}
                      placeholder="Adicionar motivo"
                      onChange={(e) => setNewReasonOption(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        addReasonOption();
                      }}
                      style={{
                        flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 13,
                        border: `1px solid ${colors.inputBorder || colors.borderColor}`,
                        backgroundColor: colors.inputBg || colors.bgSecondary,
                        color: colors.textPrimary, outline: 'none',
                      }}
                    />
                    <button
                      type="button"
                      onClick={addReasonOption}
                      disabled={!newReasonOption.trim()}
                      style={{
                        padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        cursor: newReasonOption.trim() ? 'pointer' : 'not-allowed',
                        backgroundColor: 'transparent',
                        border: `1px solid ${colors.borderColor}`,
                        color: newReasonOption.trim() ? colors.accentPrimary : colors.textSecondary,
                      }}
                    >
                      Adicionar
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ============ Atendimento que fecha sozinho ============
              Encerramento do atendimento e etapa por mensagem: só etiqueta e
              só com as flags. */}
          {!isEntry && closingUi && renderClosing()}
          {!isEntry && messageTransitionsUi && renderMessageTransitions()}

          {/* Toggle principal */}
          <section style={stepStyles.section}>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              cursor: 'pointer', padding: 12, borderRadius: 10,
              backgroundColor: autoDispatchEnabled ? `${colors.accentPrimary}08` : colors.bgSecondary,
              border: `1px solid ${autoDispatchEnabled ? colors.accentPrimary : colors.borderColor}`,
              transition: 'all 0.15s',
            }}>
              <input
                type="checkbox"
                checked={autoDispatchEnabled}
                onChange={(e) => setAutoDispatchEnabled(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Zap size={14} color={colors.accentPrimary} />
                  <strong style={{ fontSize: 13, color: colors.textPrimary }}>
                    {isEntry
                      ? 'Disparar uma mensagem quando chegar um contato novo'
                      : 'Disparar automaticamente quando um contato cair nesta etapa'}
                  </strong>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textSecondary }}>
                  Configure a mensagem ou o follow-up que será disparado automaticamente.
                </p>
              </div>
            </label>
          </section>

          {autoDispatchEnabled && (
            <>
              {/* Step 1 — Como vai disparar */}
              <section style={stepStyles.section}>
                <div style={stepStyles.head}>
                  <span style={stepStyles.num}>1</span>
                  <h4 style={stepStyles.title}>Como você quer disparar?</h4>
                </div>
                <div style={{ ...stepStyles.body, flexDirection: 'row', gap: 8 }}>
                  {choiceCard({
                    icon: Send,
                    label: 'Enviar uma mensagem',
                    desc: 'Você define o conteúdo (texto ou IA) — envio imediato ao entrar.',
                    selected: autoDispatchMode === 'inline',
                    onClick: () => setAutoDispatchMode('inline'),
                  })}
                  {choiceCard({
                    icon: Link2,
                    label: 'Atrelar a um follow-up',
                    desc: 'Adiciona o contato a um follow-up já configurado.',
                    selected: autoDispatchMode === 'advanced',
                    onClick: () => setAutoDispatchMode('advanced'),
                  })}
                </div>
              </section>

              {/* Modo INLINE */}
              {autoDispatchMode === 'inline' && (
                <>
                  {/* Canal (so quando o usuario tem os dois conectados) */}
                  {connections.qr && connections.business && (
                    <section style={stepStyles.section}>
                      <div style={stepStyles.head}>
                        <span style={stepStyles.num}>2</span>
                        <h4 style={stepStyles.title}>Por qual canal disparar?</h4>
                      </div>
                      <div style={{ ...stepStyles.body, flexDirection: 'row', gap: 8 }}>
                        {choiceCard({
                          icon: Send,
                          label: 'WhatsApp QR',
                          desc: 'Seu número pessoal conectado via QR Code.',
                          selected: selectedChannel === 'wa_personal',
                          onClick: () => setSelectedChannel('wa_personal'),
                        })}
                        {choiceCard({
                          icon: Send,
                          label: 'WhatsApp Business',
                          desc: 'API oficial da Meta. Templates e janela 24h.',
                          selected: selectedChannel === 'wa_business',
                          onClick: () => setSelectedChannel('wa_business'),
                        })}
                      </div>
                    </section>
                  )}

                  {/* Step 2 (ou 3) — Tipo de mensagem */}
                  <section style={stepStyles.section}>
                    <div style={stepStyles.head}>
                      <span style={stepStyles.num}>{connections.qr && connections.business ? 3 : 2}</span>
                      <h4 style={stepStyles.title}>Que tipo de mensagem?</h4>
                    </div>
                    <div style={{
                      ...stepStyles.body, flexDirection: 'row', gap: 8, flexWrap: 'wrap',
                    }}>
                      {choiceCard({
                        icon: Send,
                        label: 'Eu escrevo',
                        desc: 'Você digita o texto exato a enviar.',
                        selected: messageType === 'custom',
                        onClick: () => setMessageType('custom'),
                      })}
                      {/* Template Meta — so quando WA Business e canal selecionado */}
                      {selectedChannel === 'wa_business' && hasBusiness && choiceCard({
                        icon: FileText,
                        label: 'Template aprovado',
                        desc: 'Mensagem pré-aprovada da Meta. Sem janela 24h.',
                        selected: messageType === 'template',
                        onClick: () => setMessageType('template'),
                      })}
                      {choiceCard({
                        icon: Sparkles,
                        label: 'IA gera para mim',
                        desc: 'A IA monta a mensagem na hora, por contato.',
                        selected: messageType === 'ai_generated',
                        locked: !canCreateAiGenerated,
                        onClick: () => setMessageType('ai_generated'),
                      })}
                    </div>
                  </section>

                  {/* Step conteudo */}
                  <section style={stepStyles.section}>
                    <div style={stepStyles.head}>
                      <span style={stepStyles.num}>{connections.qr && connections.business ? 4 : 3}</span>
                      <h4 style={stepStyles.title}>
                        {messageType === 'template' ? 'Escolha o template aprovado'
                          : isAi ? 'O que a IA deve dizer?'
                          : 'Digite a mensagem'}
                      </h4>
                    </div>

                    <div style={stepStyles.body}>
                      {messageType === 'template' ? (
                        <>
                          <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>
                            Template aprovado
                          </label>
                          {templateUi ? (
                            <TemplatePicker
                              templates={allBusinessTemplates}
                              loading={templatesLoading}
                              value={mainTemplateName}
                              language={mainTemplateLanguage}
                              onChange={(tpl) => {
                                setMainTemplateName(tpl?.name || '');
                                if (tpl?.language) setMainTemplateLanguage(tpl.language);
                              }}
                              capabilities={greetingCaps}
                              colors={colors}
                            />
                          ) : (
                          <select
                            value={mainTemplateName}
                            onChange={(e) => {
                              const tpl = availableTemplates.find(t => t.name === e.target.value);
                              setMainTemplateName(e.target.value);
                              if (tpl?.language) setMainTemplateLanguage(tpl.language);
                            }}
                            disabled={templatesLoading}
                            style={{
                              width: '100%', padding: '10px 12px', borderRadius: 8,
                              border: `1px solid ${colors.borderColor}`,
                              backgroundColor: colors.bgPrimary,
                              color: colors.textPrimary, fontSize: 14, outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          >
                            <option value="">
                              {templatesLoading
                                ? 'Carregando...'
                                : availableTemplates.length === 0
                                  ? 'Nenhum template aprovado — crie em Templates'
                                  : 'Selecione um template...'}
                            </option>
                            {availableTemplates.map(t => (
                              <option key={t.name} value={t.name}>
                                {t.name}{t.status && t.status !== 'APPROVED' ? ` (${t.status})` : ''}
                              </option>
                            ))}
                          </select>
                          )}
                        </>
                      ) : isAi ? (
                        <>
                          {!instruction.trim() && (
                            <div>
                              <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 6, fontWeight: 500 }}>
                                Quer alguma orientação rápida? Comece por uma destas:
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {AI_SUGGESTIONS.map(s => (
                                  <button
                                    key={s.label}
                                    type="button"
                                    onClick={() => setInstruction(s.text)}
                                    style={{
                                      padding: '4px 10px', borderRadius: 999, fontSize: 11,
                                      backgroundColor: `${colors.accentPrimary}12`,
                                      color: colors.accentPrimary,
                                      border: `1px solid ${colors.accentPrimary}30`,
                                      cursor: 'pointer', fontWeight: 500,
                                    }}
                                  >
                                    {s.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <textarea
                            value={instruction}
                            onChange={(e) => setInstruction(e.target.value)}
                            placeholder="Ex: Dê boas-vindas com o primeiro nome do contato e pergunte como podemos ajudar."
                            rows={4}
                            maxLength={2000}
                            style={{
                              width: '100%', padding: '10px 12px', borderRadius: 8,
                              border: `1px solid ${colors.borderColor}`,
                              backgroundColor: colors.bgPrimary,
                              color: colors.textPrimary, fontSize: 14, outline: 'none',
                              resize: 'vertical', minHeight: 88,
                              fontFamily: 'inherit', boxSizing: 'border-box',
                            }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 11, color: colors.textTertiary }}>
                              Mínimo 10 caracteres.
                            </span>
                            <span style={{ fontSize: 11, color: colors.textTertiary }}>
                              {instruction.length}/2000
                            </span>
                          </div>

                          {/* Configurações avançadas IA */}
                          <div style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 10 }}>
                            <button
                              type="button"
                              onClick={() => setShowAiAdvanced(v => !v)}
                              style={{
                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                color: colors.textSecondary, fontSize: 12, fontWeight: 500,
                              }}
                            >
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <ShieldCheck size={13} /> Configurações avançadas da IA
                              </span>
                              {showAiAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            {showAiAdvanced && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                                <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                                  <input
                                    type="checkbox"
                                    checked={useConversationContext}
                                    onChange={(e) => setUseConversationContext(e.target.checked)}
                                    style={{ marginTop: 2 }}
                                  />
                                  <span>
                                    <strong>Considerar contexto da conversa</strong>
                                    <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                                      A IA terá acesso às últimas 15 mensagens trocadas com este contato.
                                    </div>
                                  </span>
                                </label>
                                <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                                  <input
                                    type="checkbox"
                                    checked={forceResponse}
                                    onChange={(e) => setForceResponse(e.target.checked)}
                                    style={{ marginTop: 2 }}
                                  />
                                  <span>
                                    <strong>Forçar a IA a responder sempre</strong>
                                    <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                                      Recomendado. Se desmarcar, a IA pode decidir não enviar caso julgue inadequado.
                                    </div>
                                  </span>
                                </label>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <textarea
                            value={autoDispatchText}
                            onChange={(e) => setAutoDispatchText(e.target.value)}
                            placeholder="Escreva a mensagem que será enviada quando o contato entrar nesta etapa..."
                            maxLength={4096}
                            rows={3}
                            style={{
                              width: '100%', padding: '10px 12px', borderRadius: 8,
                              border: `1px solid ${colors.borderColor}`,
                              backgroundColor: colors.bgPrimary,
                              color: colors.textPrimary, fontSize: 14, outline: 'none',
                              resize: 'vertical', minHeight: 72, maxHeight: 160,
                              fontFamily: 'inherit', boxSizing: 'border-box',
                            }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              accept={fileUploadService.getAcceptString()}
                              onChange={async (e) => {
                                const files = e.target.files;
                                if (!files || files.length === 0) return;
                                setUploading(true);
                                try {
                                  const result = await fileUploadService.uploadMultiple(files);
                                  if (result.uploaded.length > 0) {
                                    setAutoDispatchFiles(prev => [...prev, ...result.uploaded]);
                                  }
                                } catch (err) {
                                  setError('Erro ao enviar arquivo: ' + err.message);
                                } finally {
                                  setUploading(false);
                                  if (fileInputRef.current) fileInputRef.current.value = '';
                                }
                              }}
                              style={{ display: 'none' }}
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading || autoDispatchFiles.length >= 10}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: colors.accentPrimary, fontSize: 12, padding: '2px 6px',
                                borderRadius: 4, opacity: uploading ? 0.5 : 1,
                              }}
                            >
                              {uploading
                                ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                                : <Paperclip size={13} />}
                              {uploading ? 'Enviando...' : 'Anexar arquivo'}
                            </button>
                            <span style={{ fontSize: 11, color: colors.textTertiary }}>
                              {autoDispatchText.length}/4096
                            </span>
                          </div>
                          {autoDispatchFiles.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {autoDispatchFiles.map((file, idx) => {
                                const Icon = FILE_ICONS[file.type] || FileText;
                                return (
                                  <div key={idx} style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '4px 8px', borderRadius: 6,
                                    backgroundColor: colors.bgSecondary,
                                    border: `1px solid ${colors.borderColor}`,
                                    fontSize: 11, color: colors.textSecondary,
                                  }}>
                                    <Icon size={12} />
                                    <span style={{
                                      maxWidth: 120, overflow: 'hidden',
                                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>{file.filename}</span>
                                    <button
                                      type="button"
                                      onClick={() => setAutoDispatchFiles(prev => prev.filter((_, i) => i !== idx))}
                                      style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: '#ef4444', padding: '0 2px', display: 'flex',
                                      }}
                                    >
                                      <Trash2 size={11} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}

                      {/* WA Business: template fallback */}
                      {showBusinessFallback && (
                        <div style={{
                          padding: 12, borderRadius: 10,
                          backgroundColor: colors.bgSecondary,
                          border: `1px solid ${colors.borderColor}`,
                          marginTop: 4,
                        }}>
                          <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                            <input
                              type="checkbox"
                              checked={useFallbackTemplate}
                              onChange={(e) => setUseFallbackTemplate(e.target.checked)}
                              style={{ marginTop: 2 }}
                            />
                            <span>
                              <strong>Enviar uma mensagem padrão se não puder ser entregue</strong>
                              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.4 }}>
                                O WhatsApp Business só aceita mensagens livres se o cliente respondeu nas últimas 24 horas. Fora disso, enviamos a mensagem pré-aprovada que você escolher.
                              </div>
                            </span>
                          </label>
                          {useFallbackTemplate && (
                            <div style={{ marginTop: 10 }}>
                              <label style={{ display: 'block', fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>
                                Escolha a mensagem padrão
                              </label>
                              {templateUi ? (
                                <TemplatePicker
                                  templates={allBusinessTemplates}
                                  loading={templatesLoading}
                                  value={fallbackTemplateName}
                                  language={fallbackTemplateLanguage}
                                  onChange={(tpl) => {
                                    setFallbackTemplateName(tpl?.name || '');
                                    if (tpl?.language) setFallbackTemplateLanguage(tpl.language);
                                  }}
                                  capabilities={fallbackCaps}
                                  colors={colors}
                                  placeholder="Selecione uma mensagem"
                                />
                              ) : (
                              <select
                                value={fallbackTemplateName}
                                onChange={(e) => {
                                  const tpl = availableTemplates.find(t => t.name === e.target.value);
                                  setFallbackTemplateName(e.target.value);
                                  if (tpl?.language) setFallbackTemplateLanguage(tpl.language);
                                }}
                                disabled={templatesLoading}
                                style={{
                                  width: '100%', padding: '8px 10px', borderRadius: 8,
                                  border: `1px solid ${colors.borderColor}`,
                                  backgroundColor: colors.bgPrimary,
                                  color: colors.textPrimary, fontSize: 13, outline: 'none',
                                  boxSizing: 'border-box',
                                }}
                              >
                                <option value="">
                                  {templatesLoading
                                    ? 'Carregando...'
                                    : availableTemplates.length === 0
                                      ? 'Nenhuma mensagem pré-aprovada — crie em Templates'
                                      : 'Selecione uma mensagem...'}
                                </option>
                                {availableTemplates.map(t => (
                                  <option key={t.name} value={t.name}>
                                    {t.name}{t.status && t.status !== 'APPROVED' ? ` (${t.status})` : ''}
                                  </option>
                                ))}
                              </select>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Disclaimer WA Business — discreto */}
                      {isAi && selectedChannel === 'wa_business' && (
                        <div style={{
                          display: 'flex', gap: 6, alignItems: 'flex-start',
                          fontSize: 11, color: colors.textTertiary, lineHeight: 1.4,
                          padding: '4px 2px',
                        }}>
                          <AlertTriangle size={11} style={{ flexShrink: 0, marginTop: 2 }} />
                          <span>
                            Mensagens da IA só são entregues no WhatsApp Business se o contato respondeu nas últimas 24 horas.
                          </span>
                        </div>
                      )}
                    </div>
                  </section>
                </>
              )}

              {/* Modo ADVANCED — atrelar a followup */}
              {autoDispatchMode === 'advanced' && (
                <section style={stepStyles.section}>
                  <div style={stepStyles.head}>
                    <span style={stepStyles.num}>2</span>
                    <h4 style={stepStyles.title}>Atrelar a qual follow-up?</h4>
                  </div>
                  <div style={stepStyles.body}>
                    <select
                      value={autoDispatchFollowupId}
                      onChange={(e) => setAutoDispatchFollowupId(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 8,
                        border: `1px solid ${colors.borderColor}`,
                        backgroundColor: colors.bgPrimary,
                        color: colors.textPrimary, fontSize: 14, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="">— Selecione um follow-up —</option>
                      {followups.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name} {f.status === 'draft' ? '(rascunho)' : ''}
                        </option>
                      ))}
                    </select>
                    {followups.length === 0 && (
                      <p style={{
                        margin: 0, fontSize: 11, color: '#b45309',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <AlertCircle size={11} />
                        Nenhum follow-up disponível — crie um em Follow-ups primeiro.
                      </p>
                    )}
                  </div>
                </section>
              )}

              {/* Opções avançadas (compartilhadas) */}
              <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14 }}>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(v => !v)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: colors.textPrimary, fontSize: 13, fontWeight: 600,
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShieldCheck size={14} color={colors.accentPrimary} />
                    Opções avançadas de envio
                  </span>
                  {showAdvanced ? <ChevronUp size={16} color={colors.textSecondary} /> : <ChevronDown size={16} color={colors.textSecondary} />}
                </button>
                {showAdvanced && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                    <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                      <input
                        type="checkbox"
                        checked={autoDispatchRepeat}
                        onChange={(e) => setAutoDispatchRepeat(e.target.checked)}
                        style={{ marginTop: 2 }}
                      />
                      <span>
                        <strong>Reenviar quando o contato voltar para a etapa</strong>
                        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                          Por padrão, cada contato recebe a mensagem uma única vez por etiqueta.
                        </div>
                      </span>
                    </label>
                    {showAntiBlockOptions ? (
                      <>
                        <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                          <input
                            type="checkbox"
                            checked={autoDispatchAntiBlock}
                            onChange={(e) => setAutoDispatchAntiBlock(e.target.checked)}
                            style={{ marginTop: 2 }}
                          />
                          <span>
                            <strong>Espaçar envios (anti-bloqueio)</strong>
                            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                              Recomendado para WhatsApp QR — disparos acima de 50 por dia aumentam o risco de banimento. Intervalo aleatório de 8 a 15s entre envios.
                            </div>
                          </span>
                        </label>
                        <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                          <input
                            type="checkbox"
                            checked={autoDispatchTyping}
                            onChange={(e) => setAutoDispatchTyping(e.target.checked)}
                            style={{ marginTop: 2 }}
                          />
                          <span>
                            <strong>Simular digitação antes do envio</strong>
                            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                              Mostra o status "digitando..." por alguns segundos antes da mensagem.
                            </div>
                          </span>
                        </label>
                      </>
                    ) : (
                      <p style={{
                        margin: 0, fontSize: 11, color: colors.textTertiary,
                        fontStyle: 'italic',
                      }}>
                        Espaçamento e simulação de digitação se aplicam apenas ao WhatsApp QR.
                      </p>
                    )}
                  </div>
                )}
              </section>
            </>
          )}

          {/* ============ Sequência de follow-up DESTA etapa ============
              Continuação natural do auto-disparo acima: ele manda na hora, a
              sequência segue depois em degraus. Só aparece em etiqueta de
              verdade (a coluna de entrada não é etapa de funil) e só quando o
              backend libera a trilha. */}
          {!isEntry && (
            <StageTrailSection
              label={label}
              pipelineId={label?.pipeline_id}
              funnelLabels={funnelLabels}
              colors={colors}
              stepStyles={stepStyles}
              autoDispatchEnabled={autoDispatchEnabled}
            />
          )}

          {/* ============ Copiar para outro funil (multi-funil) ============
              Ao entrar nesta etapa, o contato entra também numa etapa de OUTRO
              funil e continua aqui. Ganho e perda podem apontar para etapas
              diferentes: cada etapa tem a sua. */}
          {!isEntry && multiFunnel && (() => {
            const ownFunnelId = label?.pipeline_id || '';
            const grupos = [...funnelsList]
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .filter((f) => f.id !== ownFunnelId)
              .map((f) => ({
                funnel: f,
                labels: allLabels
                  .filter((l) => l.pipeline_id === f.id)
                  .sort((a, b) => (a.order || 0) - (b.order || 0)),
              }))
              .filter((g) => g.labels.length);
            const destino = allLabels.find((l) => l.id === funnelCopyLabelId);
            const destinoFunil = destino ? funnelsList.find((f) => f.id === destino.pipeline_id) : null;
            return (
              <section style={stepStyles.section}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                  borderRadius: 10, cursor: 'pointer',
                  backgroundColor: funnelCopyEnabled ? `${colors.accentPrimary}08` : colors.bgSecondary,
                  border: `1px solid ${funnelCopyEnabled ? colors.accentPrimary : colors.borderColor}`,
                }}>
                  <input
                    type="checkbox"
                    checked={funnelCopyEnabled}
                    onChange={(e) => setFunnelCopyEnabled(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: colors.accentPrimary }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                      Copiar para outro funil ao entrar
                    </div>
                    <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.4 }}>
                      O contato entra também na etapa escolhida de outro funil e continua nesta.
                      A sequência de follow-up da etapa de destino começa; o responsável não muda.
                    </div>
                  </div>
                </label>
                {funnelCopyEnabled && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary }}>
                      Colocar em
                    </span>
                    <select
                      value={funnelCopyLabelId}
                      onChange={(e) => setFunnelCopyLabelId(e.target.value)}
                      style={{
                        padding: '9px 10px', borderRadius: 8, fontSize: 13,
                        border: `1px solid ${colors.borderColor}`,
                        backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                      }}
                    >
                      <option value="">Escolha o funil e a etapa</option>
                      {grupos.map((g) => (
                        <optgroup key={g.funnel.id} label={g.funnel.name}>
                          {g.labels.map((l) => (
                            <option key={l.id} value={l.id}>{g.funnel.name} · {l.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {grupos.length === 0 && (
                      <span style={{ fontSize: 12, color: colors.textTertiary }}>
                        Crie outro funil com etapas para poder copiar.
                      </span>
                    )}
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 4, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={funnelCopyOnlyIfEmpty}
                        onChange={(e) => setFunnelCopyOnlyIfEmpty(e.target.checked)}
                        style={{ width: 14, height: 14, marginTop: 2, accentColor: colors.accentPrimary }}
                      />
                      <span style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.4 }}>
                        Só se o contato ainda não tiver etapa nesse funil (o primeiro desfecho vale; os seguintes não sobrescrevem).
                      </span>
                    </label>
                    {destino && destinoFunil && (
                      <span style={{ fontSize: 12, color: colors.textTertiary }}>
                        Ao entrar em “{label?.name}”, o contato vai também para {destinoFunil.name} · {destino.name}.
                      </span>
                    )}
                  </div>
                )}
              </section>
            );
          })()}

          {/* ============ Distribuir o contato (fixo / round-robin) ============ */}
          {renderDistribution()}

          {/* ============ Entrada por número e resposta a disparo ============
              Só na entrada do funil padrão, que é onde o motor lê as duas. */}
          {showEntryByConnection && renderEntryByConnection()}
          {entryDefaultReady && entryRulesScreen && (
            <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 4,
              }}>
                <Link2 size={14} color={colors.accentPrimary} />
                Entrada por número
              </div>
              <p style={{ margin: 0, fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>
                O que acontece com o lead novo de cada número da equipe (fica com o dono,
                distribuição da empresa ou etapa direta) agora fica em
                <strong> Empresa › Configurações › Entrada de leads</strong>.
              </p>
            </section>
          )}
          {showReplyOwner && renderReplyOwner()}
          {isEntry && entryConfigLoaded && !entryIsDefault
            && (featureFlags.entry_by_connection_stage || featureFlags.entry_guard_assign_owner) && (
            <p style={{ margin: 0, fontSize: 11, color: colors.textTertiary }}>
              Entrada por número e resposta a disparo ficam na automação de entrada do funil padrão.
            </p>
          )}

          {/* ============ Tags ao entrar (aplicar/remover) ============ */}
          {renderEntryTags()}
          {isEntry && renderLeadSource()}

          {/* ============ Regras de aproximação (antigas, só leitura) ============
              Não se cria mais regra: a seção só aparece quando este funil ou
              etapa já tinha regra salva (item 5.7, 2026-09-13). */}
          {mostrarRegrasAntigas(savedRulesCount) && renderRules()}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderTop: `1px solid ${colors.borderColor}`,
          backgroundColor: colors.bgSecondary,
        }}>
          {error ? (
            <span style={{
              fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6,
              flex: 1, marginRight: 12,
            }}>
              <AlertCircle size={12} /> {error}
            </span>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '8px 14px', borderRadius: 8, fontSize: 13,
                backgroundColor: 'transparent', color: colors.textSecondary,
                border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 18px', backgroundColor: colors.accentPrimary,
                color: 'white', border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <Check size={14} />}
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabelConfigDrawer;
